import React from 'react'
import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components'

import Tag from 'components/lib/DesignSystem/Tag'

import { navRowActive, navRowHover } from '../navStyles'

import LockedNavItem from './LockedNavItem'

const BellDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--primary);
  flex-shrink: 0;
`

// L2/L3 sub-page row — Figma node 1:26644.
// Default: blue-950 (adaptive) regular 14px, rounded 10px pill, no background.
// Current-route ($active): 3px blue-500 left bar + Poppins Bold label + blue-50 pill.
const pageRowBase = css`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  border-radius: 10px;
  font-family: Poppins, sans-serif;
  font-size: 14px;
  /* Text hierarchy (design review 2026-08): resting rows are Medium (500); the
     current page bolds to 700 via navRowActive below. */
  font-weight: 500;
  line-height: 20px;
  color: var(--sidebar-item-text);
  text-decoration: none;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
  width: 100%;
  box-sizing: border-box;
  border: none;
  background: transparent;
  text-align: left;

  ${navRowHover}

  /* Active-page bar sits just left of the label (Figma 1:26692: the child list is
     inset ~31px and the active row is pl-4 with a 3×20 bar + 5px gap before the
     label). $barLeft is depth-aware so the bar tracks the label indent instead of
     hugging the rail's far-left edge. */
  ${({ $active, $barLeft }) => $active && navRowActive({ barLeft: $barLeft })}

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.45;
      cursor: not-allowed;
      pointer-events: none;
    `}

  /* Locked L2/L3 page (Figma rail-states §5): text de-emphasizes to gray-500,
     distinct from the L1-locked gray-300 (owned by NavSection.js). */
  ${({ $locked }) =>
    $locked &&
    css`
      color: var(--t-colors-gray-500);
    `}

  /* coming-soon: not yet built — reads as inactive, not broken. */
  ${({ $comingSoon }) =>
    $comingSoon &&
    css`
      color: var(--sub-text-color);
      cursor: default;

      &:hover {
        background-color: transparent;
        color: var(--sub-text-color);
      }
    `}
`

const SoonTag = styled.span`
  margin-left: auto;
  flex-shrink: 0;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--sub-text-color);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  padding: 1px 5px;
`

// styled-components v4 does NOT strip `$`-prefixed transient props before
// forwarding to a custom component, so styled(Link) would leak `$active`/`$disabled`
// onto the DOM <a>. Strip them here before they reach react-router's Link.
const FilteredLink = ({ $active, $disabled, $locked, $barLeft, ...rest }) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Link {...rest} />
)

// depth-aware padding is applied inline to avoid the mixed-operators lint error
const PageLink = styled(FilteredLink)`
  ${pageRowBase}
`

const PageExternalLink = styled.a`
  ${pageRowBase}
`

const PageSpan = styled.span`
  ${pageRowBase}
`

const Label = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const BellWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: auto;
`

// The L2 group is already inset 31px by its ChildrenList container in NavSection
// (Figma 1:26644, node 1:26674). NavPage therefore only carries the row's own
// in-row padding — 12px, the Link px from the design's L2 rows (1:26675) — which
// lands the text ~43px from the section edge, directly under the L1 label. Each
// deeper level (L3) steps a further 16px inside the inset group.
const BASE_INDENT = 12
const INDENT_STEP = 16

const rowIndent = (depth) => BASE_INDENT + ((depth || 1) - 1) * INDENT_STEP

// py 8px matches the design's inner-page rows (Figma 1:16633).
const depthPadding = (depth) => `8px 12px 8px ${rowIndent(depth)}px`

// Active bar sits 8px left of the label (3px bar + 5px gap, per Figma 1:26691).
const activeBarLeft = (depth) => `${rowIndent(depth) - 8}px`

/**
 * NavPage — L2/L3 leaf row.
 *
 * AC5: bell is non-numbered (dot indicator only, no count).
 * AC4: locked items are wrapped by LockedNavItem.
 *
 * @param {object} props
 * @param {string} props.id
 * @param {string} [props.dataPendoFeature] - Pendo Feature tag for this row (nav-page-*,
 *   or the nav-upsell-page-* upsell value when locked)
 * @param {string} props.label
 * @param {string} [props.resolvedPath]
 * @param {boolean} [props.active]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.locked]
 * @param {string} [props.plgPath]
 * @param {boolean} [props.hasBellNotification]
 * @param {boolean} [props.newTab]
 * @param {boolean} [props.external]
 * @param {string} [props.tag] - DS Tag type (TAGS_TYPES) rendered after the label, e.g. 'BETA'
 * @param {number} [props.depth=1] - indentation level (1 = L2, 2 = L3)
 * @param {string} [props.className]
 */
const NavPage = ({
  id,
  dataPendoFeature,
  label,
  resolvedPath,
  active,
  disabled,
  locked,
  plgPath,
  hasBellNotification,
  newTab,
  external,
  comingSoon,
  tag,
  depth = 1,
  className,
}) => {
  const padding = depthPadding(depth)
  const barLeft = activeBarLeft(depth)

  // Locked rows tag the upsell click on the LockedNavItem wrapper (the real PLG
  // click target); the inert inner row is left untagged so an entitled-page nav
  // and an upsell click never collide on one Pendo Feature.
  const rowPendoFeature = locked ? undefined : dataPendoFeature

  const bellEl = hasBellNotification ? (
    <BellWrapper aria-label="Has notifications">
      <BellDot aria-hidden="true" />
    </BellWrapper>
  ) : null

  const rowContent = (
    <>
      <Label>{label}</Label>
      {tag && <Tag type={tag} />}
      {comingSoon ? <SoonTag aria-label="Coming soon">Soon</SoonTag> : bellEl}
    </>
  )

  let inner
  if (!resolvedPath || disabled) {
    inner = (
      <PageSpan
        id={id}
        data-pendo-feature={rowPendoFeature}
        $active={active}
        $disabled={disabled}
        $comingSoon={comingSoon}
        $locked={locked}
        $barLeft={barLeft}
        style={{ padding }}
        className={className}
        aria-current={active ? 'page' : undefined}
        aria-disabled={disabled || comingSoon}
      >
        {rowContent}
      </PageSpan>
    )
  } else if (external) {
    inner = (
      <PageExternalLink
        id={id}
        href={resolvedPath}
        target={newTab ? '_blank' : '_self'}
        rel="noopener noreferrer"
        data-pendo-feature={rowPendoFeature}
        $active={active}
        $locked={locked}
        $barLeft={barLeft}
        style={{ padding }}
        className={className}
        aria-current={active ? 'page' : undefined}
      >
        {rowContent}
      </PageExternalLink>
    )
  } else {
    inner = (
      <PageLink
        id={id}
        to={resolvedPath}
        target={newTab ? '_blank' : '_self'}
        data-pendo-feature={rowPendoFeature}
        $active={active}
        $locked={locked}
        $barLeft={barLeft}
        style={{ padding }}
        className={className}
        aria-current={active ? 'page' : undefined}
      >
        {rowContent}
      </PageLink>
    )
  }

  if (locked) {
    return (
      <LockedNavItem label={label} plgPath={plgPath} dataPendoFeature={dataPendoFeature}>
        {inner}
      </LockedNavItem>
    )
  }

  return inner
}

export default NavPage
